import { useEffect, useMemo, useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useAuth } from '../../auth/AuthContext';
import HorizontalBarChart from '../../components/HorizontalBarChart';
import ModuleShell from '../../components/ModuleShell';
import PeriodDelta from '../../components/PeriodDelta';
import TimeSeriesChart from '../../components/TimeSeriesChart';
import useCurrentAndPriorQuery from '../../hooks/useCurrentAndPriorQuery';
import {
  ADMIN_TIER_DISTRIBUTION,
  ADMIN_TIER_VIOLATION_STATS,
  ADMIN_VIOLATION_COUNTS_BY_DAY,
  ADMIN_PRODUCT_ANALYTICS,
  ADMIN_PAGE_HEATMAP,
  ADMIN_PRODUCT_PATHS,
  ADMIN_VISIT_SESSIONS,
  ADMIN_VISIT_TIMELINES,
  ADMIN_ACTIVATION_FUNNEL,
} from '../../graphql/adminQueries';
import { formatNumber } from '../../utils/format';
import { RangeSelect, useDateRange } from '../home/DateRangeContext';
import {
  CONSENTED_FUNNEL_INTEGRITY_CAPTION,
  SERVER_CONVERSIONS_INTEGRITY_CAPTION,
} from '../../metrics/customerMetricsIntegrity';
import {
  funnelStepCount,
  largestAdjacentDrop,
  whyTheyLeftSignals,
} from './productDiagnosis';
import {
  VISIT_CHANNEL_FILTERS,
  VISIT_OUTCOME_FILTERS,
  filterVisitSessions,
} from './visitSessionFilters';

function sortedEntries(record) {
  return Object.entries(record || {}).sort((a, b) => b[1] - a[1]);
}

const PAID_CHANNELS = ['paid_search', 'paid_social', 'other'];

const PRODUCT_TABS = [
  { id: 'engagement', label: 'Engagement' },
  { id: 'visits', label: 'Visits' },
  { id: 'signups', label: 'Signups' },
  { id: 'friction', label: 'Friction' },
  { id: 'heatmap', label: 'Heatmap' },
];

const PRODUCT_PANE = {
  engagement: {
    title: 'Product engagement',
    description: 'Consented page views, paths, and stuck states from the client app.',
  },
  visits: {
    title: 'Product visits',
    description: 'One row is one browser tab. Filtered by the range. Rows older than 72 hours are purged. Times are Central.',
  },
  signups: {
    title: 'Product signups',
    description: 'Paid and free funnel, where they left, server conversions, and live tier snapshot.',
  },
  friction: {
    title: 'Product friction',
    description: 'Feature gates and tier restriction logs.',
  },
  heatmap: {
    title: 'Page heatmap',
    description: 'Consented click and scroll aggregates on marketing and signed-in pages. No IP or people.',
  },
};

export const PAID_FUNNEL_STEPS = [
  { id: 'pricing_page_viewed', label: 'Pricing viewed', prev: null },
  { id: 'signup_plan_step_viewed', label: 'Plan step viewed', prev: 'pricing_page_viewed' },
  { id: 'checkout_initiated', label: 'Checkout initiated', prev: 'signup_plan_step_viewed' },
  { id: 'checkout_completed', label: 'Paid checkout completed', prev: 'checkout_initiated' },
];

// Cold Free skip-plan never mounts the plan chooser, so signup_plan_step_viewed
// stays ~0 while Users grow. First step is signup page view (fires on /signup).
export const FREE_FUNNEL_STEPS = [
  { id: 'signup_page_viewed', label: 'Signup page viewed', prev: null },
  { id: 'free_signup_completed', label: 'Free signup completed', prev: 'signup_page_viewed' },
];

const EVENT_LABELS = {
  ...Object.fromEntries(PAID_FUNNEL_STEPS.map((step) => [step.id, step.label])),
  ...Object.fromEntries(FREE_FUNNEL_STEPS.map((step) => [step.id, step.label])),
  decision_template_started: 'Decision started',
  decision_template_completed: 'Decision completed',
  plaid_link_completed: 'Plaid linked',
  empty_state_shown: 'Empty state',
  form_blocked: 'Form blocked',
  validation_failed: 'Validation failed',
  page_feature_gate_shown: 'Feature gate',
  error_boundary_shown: 'Error boundary',
  signup_completed: 'Signup completed',
  subscription_started: 'Subscription started',
  activation_has_account: 'Has account',
  activation_plaid_linked: 'Plaid linked',
};

const DIMENSION_LABELS = {
  paid_search: 'Paid search',
  paid_social: 'Paid social',
  other: 'Other',
  marketing: 'Marketing',
  free: 'Free',
  basic: 'Basic',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

const PRODUCT_ANALYTICS_UNAVAILABLE =
  'Product analytics query unavailable. Deploy website-api adminProductAnalytics.';

const PRODUCT_PATHS_UNAVAILABLE =
  'Journey query unavailable. Deploy website-api migration 133 (journeys, path pairs, server conversions).';

const PAID_FUNNEL_EMPTY = 'No paid signup funnel events in range.';
const FREE_FUNNEL_EMPTY = 'No free signup funnel events in range.';
const DIAGNOSIS_CAPTION = `Event counts, not unique users. ${CONSENTED_FUNNEL_INTEGRITY_CAPTION}`;

export const ACTIVATION_CAPTION =
  'Activation rates are server milestones — not comparable to funnel steps above.';

export const ACTIVATION_STEPS = [
  { id: 'activation_has_account', label: 'Has account' },
  { id: 'activation_plaid_linked', label: 'Plaid linked' },
];

const WHY_KIND_LABELS = {
  empty: 'Empty state',
  form: 'Form blocked',
  gate: 'Feature gate',
  lastHop: 'Last hop',
};

export function humanizeAnalyticsToken(value) {
  if (value == null || value === '') return '';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
}

export function channelLabel(channel) {
  if (!channel) return '';
  return DIMENSION_LABELS[channel] || humanizeAnalyticsToken(channel);
}

function stepCountLabel(steps) {
  const count = Number(steps);
  if (!Number.isFinite(count) || count <= 0) return '';
  return count === 1 ? '1 step' : `${count} steps`;
}

export function journeyBarItem(row) {
  const detail = [channelLabel(row?.channel), stepCountLabel(row?.steps)].filter(Boolean).join(' · ');
  return {
    label: row?.path || '',
    value: row?.count ?? 0,
    detail: detail || null,
    payload: { path: row?.path || '', channel: row?.channel ?? null },
  };
}

/** Keep in sync with website-api services/productAnalyticsCollect.js APP_TIMELINE_HIDE_PREFIXES + capacitor. */
export const STOP_APP_PREFIXES = new Set([
  'dashboard',
  'accounts',
  'transactions',
  'recurring',
  'recurring-transactions',
  'cash-flow',
  'income',
  'bills',
  'budgets',
  'goals',
  'investments',
  'loans',
  'properties',
  'property-tools',
  'income-allocation',
  'net-worth',
  'reports',
  'insights',
  'assistant',
  'entities',
  'profile',
  'settings',
  'linked-accounts',
  'milestones',
  'subscriptions',
  'change-password',
  'change_password',
  'wizards',
  'category-rules',
  'achievements',
  'planning',
  'capacitor',
]);

export function stopBarsFromJourneys(journeys, { hideApp } = {}) {
  if (!journeys) return [];
  const totals = new Map();
  for (const row of journeys) {
    if (!row?.path || row.count == null) continue;
    const segments = row.path.split(' → ');
    const pagePath = segments[segments.length - 1];
    if (!pagePath) continue;
    if (hideApp) {
      const head = pagePath.split('?')[0].replace(/^\//, '').split('/')[0];
      if (STOP_APP_PREFIXES.has(head)) continue;
    }
    const count = Number(row.count) || 0;
    totals.set(pagePath, (totals.get(pagePath) || 0) + count);
  }
  const bars = [];
  for (const [pagePath, count] of totals.entries()) {
    if (count < 3) continue;
    bars.push({ label: pagePath, value: count });
  }
  bars.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.label.localeCompare(b.label);
  });
  return bars.slice(0, 40);
}

export function visitHopLabel(hop) {
  const pathLabel = hop?.path || '';
  const eventName = hop?.eventName || '';
  if (!eventName || eventName === 'page_view' || pathLabel === '(app)') return pathLabel;
  const eventLabel = EVENT_LABELS[eventName] || humanizeAnalyticsToken(eventName);
  return eventLabel ? `${pathLabel} · ${eventLabel}` : pathLabel;
}

export function visitTimelineErrorCopy(error) {
  const text = error?.message || '';
  if (/rate limit/i.test(text)) return 'Too many visit reads this hour. Try again later.';
  return 'Visit list unavailable. Deploy website-api adminVisitTimelines.';
}

export function visitSessionsErrorCopy(error) {
  const text = error?.message || '';
  if (/rate limit/i.test(text)) return 'Too many visit reads this hour. Try again later.';
  return 'Visit list unavailable.';
}

function formatVisitInstant(iso, { seconds = false } = {}) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const options = {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  if (seconds) options.second = '2-digit';
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

function chicagoParts(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value])
  );
  return {
    day: `${parts.month} ${parts.day}`,
    clock: `${parts.hour}:${parts.minute} ${parts.dayPeriod}`,
  };
}

export function visitWhen(session) {
  const hops = session?.hops || [];
  const start = chicagoParts(hops[0]?.at || session?.startedAt);
  const end = chicagoParts(hops[hops.length - 1]?.at || session?.endedAt);
  if (!start && !end) return '';
  if (!start || !end || (start.day === end.day && start.clock === end.clock)) {
    const only = start || end;
    return `${only.day}, ${only.clock}`;
  }
  if (start.day === end.day) return `${start.day}, ${start.clock} – ${end.clock}`;
  return `${start.day}, ${start.clock} – ${end.day}, ${end.clock}`;
}

export function visitDurationLabel(session) {
  const hops = session?.hops || [];
  const start = new Date(session?.startedAt || hops[0]?.at);
  const end = new Date(session?.endedAt || hops[hops.length - 1]?.at);
  const ms = end.getTime() - start.getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 60000) return 'under 1 min';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours} hr ${rem} min` : `${hours} hr`;
}

export function visitPageCountLabel(hops) {
  const count = (hops || []).filter((hop) => (typeof hop === 'string' ? hop : hop?.path)).length;
  if (!count) return '';
  return count === 1 ? '1 page' : `${count} pages`;
}

export function visitOutcome(session) {
  const paths = (session?.hops || [])
    .map((hop) => (typeof hop === 'string' ? hop : hop?.path))
    .filter(Boolean);
  const last = paths[paths.length - 1] || '';
  if (!last) return '';
  return last === '/signup/success' ? 'reached /signup/success' : `stopped at ${last}`;
}

export function collapseVisitHops(hops) {
  const groups = [];
  for (const hop of hops || []) {
    const path = typeof hop === 'string' ? hop : hop?.path || '';
    if (!path) continue;
    const prev = groups[groups.length - 1];
    if (prev && prev.path === path) {
      prev.count += 1;
    } else {
      groups.push({ path, count: 1 });
    }
  }
  return groups;
}

export function visitChipWindow(groups, max = 6) {
  if (groups.length <= max) return groups.map((group) => ({ ...group, ellipsis: false }));
  return [
    ...groups.slice(0, 3).map((group) => ({ ...group, ellipsis: false })),
    { ellipsis: true },
    ...groups.slice(-2).map((group) => ({ ...group, ellipsis: false })),
  ];
}

const VISIT_PAUSE_MS = 15 * 60 * 1000;

export function formatDwell(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return 'under 1 sec';
  if (ms < 60000) {
    const secs = Math.round(ms / 1000);
    return secs === 1 ? '1 sec' : `${secs} sec`;
  }
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours} hr ${rem} min` : `${hours} hr`;
}

function visitGapMs(hops) {
  const gaps = [];
  for (let index = 0; index < hops.length - 1; index += 1) {
    const ms = new Date(hops[index + 1].at).getTime() - new Date(hops[index].at).getTime();
    if (!Number.isFinite(ms) || ms < 0) continue;
    gaps.push({ path: hops[index].path, next: hops[index + 1].path, ms });
  }
  return gaps;
}

export function visitClockFacts(session) {
  const iso = session?.startedAt || session?.hops?.[0]?.at;
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return { weekday: '', daypart: '' };
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      weekday: 'long',
      hour: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value])
  );
  const hour = Number(parts.hour);
  let daypart = 'night';
  if (hour >= 5 && hour < 12) daypart = 'morning';
  else if (hour >= 12 && hour < 17) daypart = 'afternoon';
  else if (hour >= 17 && hour < 21) daypart = 'evening';
  return { weekday: parts.weekday || '', daypart };
}

export function visitHopDwell(hop, nextHop) {
  if (!hop?.at || !nextHop?.at) return '';
  return formatDwell(new Date(nextHop.at).getTime() - new Date(hop.at).getTime());
}

export function visitFacts(session) {
  const hops = (session?.hops || []).filter((hop) => hop?.path);
  const unique = new Set(hops.map((hop) => hop.path)).size;
  const counts = new Map();
  for (const hop of hops) counts.set(hop.path, (counts.get(hop.path) || 0) + 1);
  const gaps = visitGapMs(hops);
  let longest = null;
  let shortest = null;
  let pauses = 0;
  let activeMs = 0;
  let pausedMs = 0;
  let glanced = 0;
  let stayed = 0;
  let backtracks = 0;
  for (const gap of gaps) {
    if (gap.ms >= VISIT_PAUSE_MS) {
      pauses += 1;
      pausedMs += gap.ms;
    } else {
      activeMs += gap.ms;
    }
    if (gap.ms < 10000) glanced += 1;
    if (gap.ms >= 120000) stayed += 1;
    if (!longest || gap.ms > longest.ms) longest = gap;
    if (!shortest || gap.ms < shortest.ms) shortest = gap;
  }
  for (let index = 0; index < hops.length - 2; index += 1) {
    if (hops[index].path === hops[index + 2].path && hops[index].path !== hops[index + 1].path) {
      backtracks += 1;
    }
  }
  const spanMs =
    hops.length > 1
      ? new Date(hops[hops.length - 1].at).getTime() - new Date(hops[0].at).getTime()
      : 0;
  const clock = visitClockFacts(session);
  return {
    pages: hops.length,
    unique,
    repeats: Math.max(0, hops.length - unique),
    repeatPaths: [...counts.entries()]
      .filter(([, count]) => count > 1)
      .map(([path, count]) => `${path} ×${count}`),
    entry: hops[0]?.path || '',
    exit: hops[hops.length - 1]?.path || '',
    returned: hops.length > 1 && hops[0].path === hops[hops.length - 1].path,
    pauses,
    active: activeMs ? formatDwell(activeMs) : '',
    paused: pausedMs ? formatDwell(pausedMs) : '',
    longestPath: longest?.path || '',
    longestDwell: longest ? formatDwell(longest.ms) : '',
    longestShare:
      longest && spanMs > 0 ? `${Math.round((longest.ms / spanMs) * 100)}%` : '',
    shortestPath: shortest?.path || '',
    shortestDwell: shortest ? formatDwell(shortest.ms) : '',
    glanced,
    stayed,
    backtracks,
    weekday: clock.weekday,
    daypart: clock.daypart,
  };
}

export function visitHopLine(hop) {
  const when = formatVisitInstant(hop?.at, { seconds: true });
  return when ? `${when} · ${hop.path}` : hop?.path || '';
}

export function pathPairBarItem(row) {
  const fromPath = row?.fromPath || '';
  const toPath = row?.toPath || '';
  return {
    label: fromPath && toPath ? `${fromPath} → ${toPath}` : fromPath || toPath,
    value: row?.count ?? 0,
    detail: channelLabel(row?.channel) || null,
  };
}

export function analyticsBarItem(row, { eventLabels = EVENT_LABELS } = {}) {
  const path = row?.pagePath || '';
  const looksLikeEvent = eventLabels[path] || !path.startsWith('/');
  return {
    label: looksLikeEvent ? eventLabels[path] || humanizeAnalyticsToken(path) : path,
    value: row?.count ?? 0,
    detail: channelLabel(row?.dimension) || null,
  };
}

function formatStepRate(current, previous) {
  if (!previous) return '—';
  return `${Math.round((current / previous) * 100)}%`;
}

export function formatFunnelStepRate(_stepId, current, previous) {
  return formatStepRate(current, previous);
}

export function isFunnelTableEmpty(funnelRows, steps) {
  return PAID_CHANNELS.every((channel) =>
    steps.every((step) => funnelStepCount(funnelRows, step.id, channel) === 0)
  );
}

function whyTheyLeftBarItem(signal) {
  const kindLabel = WHY_KIND_LABELS[signal.kind] || signal.kind;
  if (signal.kind === 'lastHop') {
    return {
      label: signal.pagePath,
      value: signal.count,
      detail: kindLabel,
    };
  }
  const item = analyticsBarItem({
    pagePath: signal.pagePath,
    dimension: signal.dimension,
    count: signal.count,
  });
  return {
    ...item,
    detail: [kindLabel, item.detail].filter(Boolean).join(' · '),
  };
}

function AnalyticsBars({
  title,
  hint,
  unavailable,
  error,
  loading,
  loadingLabel,
  items,
  emptyMessage,
  onRowActivate,
  extra,
}) {
  return (
    <section className="chart-card">
      <h2>{title}</h2>
      <p className="chart-hint">{hint}</p>
      {error ? (
        <p className="chart-empty">{unavailable}</p>
      ) : loading ? (
        <p className="chart-hint">{loadingLabel}</p>
      ) : (
        <HorizontalBarChart
          items={items}
          emptyMessage={emptyMessage}
          onRowActivate={onRowActivate}
        />
      )}
      {extra}
    </section>
  );
}

function SignupFunnelTable({ title, hint, emptyMessage, steps, funnelRows, analyticsError, analyticsLoading }) {
  return (
    <section className="chart-card">
      <h2>{title}</h2>
      <p className="chart-hint">{hint}</p>
      {analyticsError ? (
        <p className="chart-empty">{PRODUCT_ANALYTICS_UNAVAILABLE}</p>
      ) : analyticsLoading ? (
        <p className="chart-hint">Loading funnel events…</p>
      ) : isFunnelTableEmpty(funnelRows, steps) ? (
        <p className="chart-empty">{emptyMessage}</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table funnel-table">
            <thead>
              <tr>
                <th scope="col">Step</th>
                {PAID_CHANNELS.map((channel) => (
                  <th key={channel} scope="col" colSpan={2}>
                    {channelLabel(channel)}
                  </th>
                ))}
              </tr>
              <tr>
                <th scope="col" />
                {PAID_CHANNELS.map((channel) => [
                  <th key={`${channel}-n`} scope="col">
                    Count
                  </th>,
                  <th key={`${channel}-rate`} scope="col">
                    % prev
                  </th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {steps.map((step) => (
                <tr key={step.id}>
                  <td>{step.label}</td>
                  {PAID_CHANNELS.map((channel) => {
                    const count = funnelStepCount(funnelRows, step.id, channel);
                    const previous = step.prev ? funnelStepCount(funnelRows, step.prev, channel) : 0;
                    return [
                      <td key={`${step.id}-${channel}-n`}>{formatNumber(count)}</td>,
                      <td key={`${step.id}-${channel}-rate`}>
                        {formatFunnelStepRate(step.id, count, previous)}
                      </td>,
                    ];
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SignupDiagnosis({
  steps,
  funnelRows,
  emptyStates,
  formBlocked,
  featureGates,
  journeys,
  analyticsError,
  analyticsLoading,
  pathsError,
  pathsLoading,
}) {
  const drop = largestAdjacentDrop(steps, funnelRows);
  const why = whyTheyLeftSignals({
    emptyStates: analyticsError ? [] : emptyStates,
    formBlocked: analyticsError ? [] : formBlocked,
    featureGates: analyticsError ? [] : featureGates,
    journeys: pathsError ? [] : journeys,
  });
  const whyError = Boolean(analyticsError && pathsError);
  const whyLoading = analyticsLoading || pathsLoading;

  return (
    <>
      <section className="chart-card">
        <h2>Largest drop</h2>
        <p className="chart-hint">
          Paid funnel adjacent step with the biggest count drop (totals across channels).{' '}
          {DIAGNOSIS_CAPTION}
        </p>
        {analyticsError ? (
          <p className="chart-empty">{PRODUCT_ANALYTICS_UNAVAILABLE}</p>
        ) : analyticsLoading ? (
          <p className="chart-hint">Loading funnel events…</p>
        ) : !drop || drop.drop <= 0 ? (
          <p className="chart-empty">No adjacent drop in this range.</p>
        ) : (
          <p>
            <strong>
              {drop.fromLabel} → {drop.toLabel}
            </strong>
            {`: ${formatNumber(drop.fromCount)} → ${formatNumber(drop.toCount)} (−${formatNumber(
              drop.drop
            )}, ${formatFunnelStepRate(drop.toId, drop.toCount, drop.fromCount)} of previous)`}
          </p>
        )}
      </section>

      <AnalyticsBars
        title="Why they left"
        hint={`Top empty states, feature gates, blocked forms, and journey last hops. ${DIAGNOSIS_CAPTION}`}
        unavailable={PRODUCT_ANALYTICS_UNAVAILABLE}
        error={whyError}
        loading={whyLoading}
        loadingLabel="Loading diagnosis signals…"
        items={why.map((signal) => whyTheyLeftBarItem(signal))}
        emptyMessage="No empty-state, gate, form, or last-hop signals in range."
      />
    </>
  );
}

function activationStepCount(steps, id) {
  const row = (steps || []).find((step) => step.id === id);
  return Number(row?.count) || 0;
}

function SignupActivation({ steps, error, loading }) {
  return (
    <details className="chart-card">
      <summary>Activation</summary>
      <p className="chart-hint">{ACTIVATION_CAPTION}</p>
      {error ? (
        <p className="chart-empty">Activation query unavailable. Deploy website-api adminActivationFunnel.</p>
      ) : loading ? (
        <p className="chart-hint">Loading activation…</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Step</th>
                <th scope="col">Count</th>
              </tr>
            </thead>
            <tbody>
              {ACTIVATION_STEPS.map((step) => (
                <tr key={step.id}>
                  <td>{step.label}</td>
                  <td>{formatNumber(activationStepCount(steps, step.id))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}

const HEATMAP_DEVICES = [
  { id: '', label: 'All devices' },
  { id: 'desktop', label: 'Desktop' },
  { id: 'tablet', label: 'Tablet' },
  { id: 'mobile', label: 'Mobile' },
];

const HEATMAP_CHANNELS = [
  { id: '', label: 'All channels' },
  { id: 'paid_search', label: 'Paid search' },
  { id: 'paid_social', label: 'Paid social' },
  { id: 'other', label: 'Other' },
];

function heatOpacity(count, max) {
  if (!max) return 0;
  return 0.12 + (count / max) * 0.78;
}

function HeatmapPane({ range }) {
  const [path, setPath] = useState('');
  const [device, setDevice] = useState('');
  const [channel, setChannel] = useState('');
  const { data, loading, error } = useQuery(ADMIN_PAGE_HEATMAP, {
    variables: {
      path: path || null,
      range,
      device: device || null,
      channel: channel || null,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const heatmap = data?.adminPageHeatmap;
  const paths = heatmap?.paths || [];
  const clicks = heatmap?.clicks || [];
  const scroll = heatmap?.scroll || [];
  const selectors = heatmap?.selectors || [];
  const maxClick = clicks.reduce((max, cell) => Math.max(max, cell.count), 0);
  const maxScroll = scroll.reduce((max, row) => Math.max(max, row.count), 0);
  const clickMap = new Map(clicks.map((cell) => [`${cell.xBucket}|${cell.yBucket}`, cell.count]));
  const scrollMap = new Map(scroll.map((row) => [row.depthBucket, row.count]));

  return (
    <>
      <section className="chart-card">
        <h2>Click density</h2>
        <p className="chart-hint">
          Consented click and scroll aggregates. Not people. A cell is not proof a signed-in user clicked. Clicks are not checkouts. Cells under {heatmap?.minCount ?? 3} stay hidden. Account ids are folded to :id.
        </p>
        <div className="heatmap-filters">
          <label>
            Page
            <select
              value={path}
              onChange={(event) => setPath(event.target.value)}
            >
              <option value="">Choose a page</option>
              {paths.map((row) => (
                <option key={row.pagePath} value={row.pagePath}>
                  {row.pagePath} ({formatNumber(row.count)})
                </option>
              ))}
            </select>
          </label>
          <label>
            Device
            <select value={device} onChange={(event) => setDevice(event.target.value)}>
              {HEATMAP_DEVICES.map((row) => (
                <option key={row.id || 'all'} value={row.id}>
                  {row.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Channel
            <select value={channel} onChange={(event) => setChannel(event.target.value)}>
              {HEATMAP_CHANNELS.map((row) => (
                <option key={row.id || 'all'} value={row.id}>
                  {row.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error ? (
          <p className="chart-empty">Could not load the heatmap.</p>
        ) : loading ? (
          <p className="chart-hint">Loading heatmap…</p>
        ) : !path ? (
          <p className="chart-empty">
            {paths.length === 0
              ? 'No heatmap cells in range (need 3+ consented clicks).'
              : 'Choose a page to plot clicks.'}
          </p>
        ) : clicks.length === 0 ? (
          <p className="chart-empty">No click cells for this page in range.</p>
        ) : (
          <div className="heatmap-grid" role="img" aria-label="Click density grid">
            {Array.from({ length: 20 }, (_, y) =>
              Array.from({ length: 20 }, (_, x) => {
                const count = clickMap.get(`${x}|${y}`) || 0;
                return (
                  <div
                    key={`${x}-${y}`}
                    className="heatmap-cell"
                    style={{
                      background:
                        count > 0
                          ? `rgba(180, 83, 9, ${heatOpacity(count, maxClick)})`
                          : 'transparent',
                    }}
                    title={count > 0 ? `${count} clicks` : undefined}
                  />
                );
              })
            )}
          </div>
        )}
      </section>

      <section className="chart-card">
        <h2>Scroll depth</h2>
        <p className="chart-hint">Scroll depth aggregates. Not people. Same 3-count floor.</p>
        {error ? (
          <p className="chart-empty">Could not load scroll depth.</p>
        ) : loading ? (
          <p className="chart-hint">Loading scroll depth…</p>
        ) : !path ? (
          <p className="chart-empty">Choose a page to plot scroll.</p>
        ) : scroll.length === 0 ? (
          <p className="chart-empty">No scroll depth for this page in range.</p>
        ) : (
          <div className="heatmap-scroll" aria-label="Scroll depth">
            {Array.from({ length: 20 }, (_, depth) => {
              const count = scrollMap.get(depth) || 0;
              return (
                <div
                  key={depth}
                  className="heatmap-scroll-cell"
                  style={{
                    background:
                      count > 0
                        ? `rgba(0, 102, 204, ${heatOpacity(count, maxScroll)})`
                        : '#E5E7EB',
                  }}
                  title={count > 0 ? `${count} at ${depth * 5}%` : undefined}
                />
              );
            })}
          </div>
        )}
      </section>

      <section className="chart-card">
        <h2>Hottest controls</h2>
        <p className="chart-hint">Sanitized selectors. Never a visitor id.</p>
        {error ? (
          <p className="chart-empty">Could not load selectors.</p>
        ) : loading ? (
          <p className="chart-hint">Loading selectors…</p>
        ) : !path ? (
          <p className="chart-empty">Choose a page to list controls.</p>
        ) : selectors.length === 0 ? (
          <p className="chart-empty">No selectors for this page in range.</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Selector</th>
                  <th scope="col">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {selectors.map((row) => (
                  <tr key={row.selector}>
                    <td>{row.selector}</td>
                    <td>{formatNumber(row.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function VisitPathChips({ hops }) {
  const windowed = visitChipWindow(collapseVisitHops(hops));
  return (
    <span className="visit-chips">
      {windowed.map((group, index) =>
        group.ellipsis ? (
          <span key="ellipsis" className="visit-arrow">
            …
          </span>
        ) : (
          <span key={`${group.path}-${index}`} className="visit-chip-wrap">
            {index > 0 ? <span className="visit-arrow">→</span> : null}
            <span className="visit-chip">
              {group.path}
              {group.count > 1 ? <span className="visit-chip-count"> ×{group.count}</span> : null}
            </span>
          </span>
        )
      )}
    </span>
  );
}

function visitIdentity(session) {
  const email = typeof session?.email === 'string' ? session.email.trim() : '';
  const displayName = typeof session?.displayName === 'string' ? session.displayName.trim() : '';
  return { email, displayName };
}

function VisitFacts({ session }) {
  const facts = visitFacts(session);
  const identity = visitIdentity(session);
  const items = [
    ['Who', identity.displayName],
    ['Email', identity.email],
    ['Channel', channelLabel(session?.channel)],
    ['When', visitWhen(session)],
    ['Weekday', facts.weekday],
    ['Time of day', facts.daypart],
    ['Duration', visitDurationLabel(session)],
    ['Active', facts.pauses ? facts.active : ''],
    ['Paused', facts.paused],
    ['Pages', visitPageCountLabel(session.hops)],
    ['Unique pages', facts.unique ? String(facts.unique) : ''],
    ['Repeated pages', facts.repeatPaths.length ? facts.repeatPaths.join(', ') : '0'],
    ['Backtracks', facts.backtracks ? String(facts.backtracks) : ''],
    ['Returned to entry', facts.returned ? 'yes' : ''],
    ['Entry', facts.entry],
    ['Stopped', visitOutcome(session)],
    ['Longest page', facts.longestPath ? `${facts.longestPath} · ${facts.longestDwell}` : ''],
    ['Longest share', facts.longestShare],
    [
      'Shortest page',
      facts.pages > 2 && facts.shortestPath
        ? `${facts.shortestPath} · ${facts.shortestDwell}`
        : '',
    ],
    ['Glanced under 10 sec', facts.glanced ? String(facts.glanced) : ''],
    ['Stayed 2 min or more', facts.stayed ? String(facts.stayed) : ''],
    ['Pauses of 15 min', facts.pauses ? String(facts.pauses) : '0'],
  ].filter(([, value]) => value);
  return (
    <dl className="visit-facts">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function VisitInteractions({ interactions }) {
  const rows = interactions || [];
  return (
    <section className="visit-interactions" aria-label="Visit interactions">
      <h3>Interactions</h3>
      {rows.length === 0 ? (
        <p className="chart-hint">No clicks, form, or funnel events recorded on this tab.</p>
      ) : (
        <ol className="visit-hops">
          {rows.map((row, index) => (
            <li key={`${row.at || 'event'}-${row.eventName}-${index}`} className="visit-hop">
              <span>
                {EVENT_LABELS[row.eventName] || humanizeAnalyticsToken(row.eventName)}
                {row.path ? ` · ${row.path}` : ''}
              </span>
              <span className="visit-dwell">{formatVisitInstant(row.at, { seconds: true })}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function VisitDetail({ session }) {
  const hops = session?.hops || [];
  return (
    <section className="visit-detail" aria-label="Visit detail">
      <VisitFacts session={session} />
      <VisitPathChips hops={hops} />
      <ol className="visit-hops">
        {hops.map((hop, index) => {
          const dwell = visitHopDwell(hop, hops[index + 1]);
          return (
            <li key={`${hop.at || 'hop'}-${index}`} className="visit-hop">
              <span>{visitHopLine(hop)}</span>
              <span className="visit-dwell">{dwell ? `${dwell} on this page` : 'left'}</span>
            </li>
          );
        })}
      </ol>
      <VisitInteractions interactions={session?.interactions} />
    </section>
  );
}

function VisitList({ isAdmin, loading, error, rows, loaded }) {
  const [selected, setSelected] = useState(0);
  const [query, setQuery] = useState('');
  const [channel, setChannel] = useState('');
  const [outcome, setOutcome] = useState('');
  const source = rows || [];
  const list = useMemo(
    () => filterVisitSessions(rows, { query, channel, outcome }),
    [rows, query, channel, outcome]
  );
  const selectedIndex = list.length === 0 ? -1 : Math.min(selected, list.length - 1);
  const selectedRow = selectedIndex >= 0 ? list[selectedIndex] : null;
  const hasFilters = Boolean(query.trim() || channel || outcome);

  if (!isAdmin) {
    return <p className="chart-empty">Individual visits are an admin read.</p>;
  }
  return (
    <>
      <div className="heatmap-filters" role="search">
        <label htmlFor="visit-search">
          Search
          <input
            id="visit-search"
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelected(0);
            }}
            placeholder="Path, email, channel, event"
            autoComplete="off"
          />
        </label>
        <label htmlFor="visit-channel">
          Channel
          <select
            id="visit-channel"
            value={channel}
            onChange={(event) => {
              setChannel(event.target.value);
              setSelected(0);
            }}
          >
            {VISIT_CHANNEL_FILTERS.map((row) => (
              <option key={row.id || 'all'} value={row.id}>
                {row.label}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="visit-outcome">
          Outcome
          <select
            id="visit-outcome"
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value);
              setSelected(0);
            }}
          >
            {VISIT_OUTCOME_FILTERS.map((row) => (
              <option key={row.id || 'all'} value={row.id}>
                {row.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {loading ? <p className="chart-hint">Loading visits…</p> : null}
      {error ? <p className="chart-empty">{visitSessionsErrorCopy(error)}</p> : null}
      {loaded && !loading && !error && source.length === 0 ? (
        <p className="chart-empty">No visits in this range (rows older than 72 hours are purged).</p>
      ) : null}
      {loaded && !loading && !error && source.length > 0 && list.length === 0 ? (
        <p className="chart-empty">No visits match this search.</p>
      ) : null}
      {!loading && !error && list.length > 0 ? (
        <>
          <p className="visit-count">
            {hasFilters
              ? `${formatNumber(list.length)} of ${formatNumber(source.length)} ${
                  source.length === 1 ? 'visit' : 'visits'
                }`
              : list.length === 1
                ? '1 visit'
                : `${formatNumber(list.length)} visits`}
          </p>
          <div className="visit-split">
            <ol className="visit-list">
              {list.map((row, index) => {
                const identity = visitIdentity(row);
                const who = identity.email || identity.displayName;
                const meta = [
                  who,
                  channelLabel(row.channel),
                  visitPageCountLabel(row.hops),
                  visitDurationLabel(row),
                  visitFacts(row).daypart,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <li key={`${row.startedAt || 'visit'}-${index}`}>
                    <button
                      type="button"
                      className="visit-pick"
                      aria-pressed={index === selectedIndex}
                      onClick={() => setSelected(index)}
                    >
                      <span className="visit-when">{visitWhen(row)}</span>
                      {meta ? <span className="visit-meta">{meta}</span> : null}
                      <span className="visit-outcome">{visitOutcome(row)}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {selectedRow ? <VisitDetail session={selectedRow} /> : null}
          </div>
        </>
      ) : null}
      <p className="module-footnote">
        Search, channel, and outcome apply to the loaded list (up to 50 tabs) in the selected
        range. TikTok and click ids are stored as Paid social. Raw referrer, UTM, device, and
        country are not on visit rows. Email shows only when collect stamped a signed-in user.
        Rows can include collect writes. Not a person.
      </p>
    </>
  );
}

export default function ProductModule() {
  const { range, visitRange } = useDateRange();
  const { isAdmin } = useAuth();
  const rangeKey = `${range.from}|${range.to}`;
  const visitRangeKey = `${visitRange?.from || ''}|${visitRange?.to || ''}`;
  const [trackedRange, setTrackedRange] = useState(rangeKey);
  const [openJourneyPayload, setOpenJourneyPayload] = useState(null);
  if (trackedRange !== rangeKey) {
    setTrackedRange(rangeKey);
    setOpenJourneyPayload(null);
  }

  const {
    data: tierData,
    loading: tierLoading,
    error: tierError,
  } = useQuery(ADMIN_TIER_DISTRIBUTION, {
    variables: { includeInactive: true },
  });

  const {
    data: violationData,
    priorData: priorViolationData,
    loading: violationLoading,
    error: violationError,
  } = useCurrentAndPriorQuery(ADMIN_TIER_VIOLATION_STATS, range);

  const {
    data: limitHitData,
    loading: limitHitLoading,
    error: limitHitError,
  } = useQuery(ADMIN_TIER_VIOLATION_STATS, {
    variables: { range, reason: 'limit_exceeded' },
  });

  const {
    data: violationDayData,
    loading: violationDayLoading,
    error: violationDayError,
  } = useQuery(ADMIN_VIOLATION_COUNTS_BY_DAY, {
    variables: { range },
  });

  const {
    data: productAnalyticsData,
    loading: productAnalyticsLoading,
    error: productAnalyticsError,
  } = useQuery(ADMIN_PRODUCT_ANALYTICS, {
    variables: { range },
    errorPolicy: 'all',
  });

  const {
    data: productPathsData,
    loading: productPathsLoading,
    error: productPathsError,
  } = useQuery(ADMIN_PRODUCT_PATHS, {
    variables: { range },
    errorPolicy: 'all',
    // Same root field has no cache id; a cached write would drop page views.
    fetchPolicy: 'no-cache',
  });

  const [loadVisitTimelines, visitTimelineQuery] = useLazyQuery(ADMIN_VISIT_TIMELINES, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const [loadVisitSessions, visitSessionsQuery] = useLazyQuery(ADMIN_VISIT_SESSIONS, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const [activeTab, setActiveTab] = useState('engagement');

  const {
    data: activationData,
    loading: activationLoading,
    error: activationError,
  } = useQuery(ADMIN_ACTIVATION_FUNNEL, {
    variables: { range },
    errorPolicy: 'all',
    skip: activeTab !== 'signups',
  });

  useEffect(() => {
    if (activeTab !== 'visits' || !isAdmin) return;
    loadVisitSessions({
      variables: { range: { from: visitRange.from, to: visitRange.to } },
    });
  }, [activeTab, isAdmin, visitRangeKey, loadVisitSessions, visitRange.from, visitRange.to]);

  function openJourney(payload) {
    if (!isAdmin || !payload?.path) return;
    setOpenJourneyPayload(payload);
    loadVisitTimelines({
      variables: {
        path: payload.path,
        channel: payload.channel,
        range: { from: range.from, to: range.to },
        limit: 20,
      },
    });
  }

  const timelineVars = visitTimelineQuery.variables;
  const timelineIsCurrent =
    timelineVars?.path === openJourneyPayload?.path &&
    timelineVars?.channel === openJourneyPayload?.channel &&
    timelineVars?.range?.from === range.from &&
    timelineVars?.range?.to === range.to;

  const violations = violationData?.adminTierViolationStats;
  const priorViolations = priorViolationData?.adminTierViolationStats;
  const limitHits = limitHitData?.adminTierViolationStats;
  const violationDaySeries = violationDayData?.adminViolationCountsByDay || [];
  const productAnalytics = productAnalyticsData?.adminProductAnalytics;
  const productPaths = productPathsData?.adminProductAnalytics;
  const distribution = tierData?.adminTierDistribution || [];
  const tierBars = distribution.map((entry) => [entry.tier || entry.slug, entry.count]);
  const limitHitBars = sortedEntries(limitHits?.byAction);
  const byActionRows = sortedEntries(violations?.byAction);
  const byReasonRows = sortedEntries(violations?.byReason);
  const byTierRows = sortedEntries(violations?.byTier);
  const violationTables = [
    ['Top friction by action', byActionRows, 'No violations by action.'],
    ['Violations by reason', byReasonRows, 'No violations by reason.'],
    ['Violations by tier', byTierRows, 'No violations by tier.'],
  ];

  const hasViolationData = (violations?.total ?? 0) > 0;
  const funnelRows = productAnalytics?.funnel;
  const pane = PRODUCT_PANE[activeTab] || PRODUCT_PANE.engagement;

  return (
    <ModuleShell
      title={pane.title}
      description={pane.description}
      actions={<RangeSelect id="product-range" />}
    >
      <nav className="economics-tabs" aria-label="Product sections">
        {PRODUCT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={
              activeTab === tab.id ? 'economics-tab economics-tab-active' : 'economics-tab'
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'engagement' && (
        <>
          <AnalyticsBars
            title="Pages people visit"
            hint="Consented page views. Sanitized paths."
            unavailable={PRODUCT_ANALYTICS_UNAVAILABLE}
            error={productAnalyticsError}
            loading={productAnalyticsLoading}
            loadingLabel="Loading page views…"
            items={(productAnalytics?.pageViews || []).map((row) => analyticsBarItem(row))}
            emptyMessage="No consented page views in range. Users must accept analytics cookies."
          />

          <div className="product-grid">
            <div style={{ display: 'grid', gap: '1rem' }}>
              <AnalyticsBars
                title="Paths people take"
                hint={
                  isAdmin
                    ? 'Counts are visits kept 72 hours. Open a bar to read those visits. The bar count is not the length of the list (capped at 20).'
                    : 'Selected range. Raw steps are kept 72 hours, so earlier days in a long range are empty.'
                }
                unavailable={PRODUCT_PATHS_UNAVAILABLE}
                error={productPathsError}
                loading={productPathsLoading}
                loadingLabel="Loading journeys…"
                items={(productPaths?.journeys || []).map((row) => journeyBarItem(row))}
                emptyMessage="No journeys in range. Raw steps are kept 72 hours."
                onRowActivate={isAdmin ? openJourney : undefined}
              />

              <AnalyticsBars
                title="Last page of this list."
                hint={
                  isAdmin
                    ? 'Last page of the journeys list above (top 50, max 12 unique hops, open tabs in); selected range; raw steps kept 72 hours; not session exit; not Step-to-step; not Visits “stopped at”; not people.'
                    : 'Last page of the journeys list above (top 50, max 12 unique hops, open tabs in); selected range; raw steps kept 72 hours; books last hops omitted; not session exit; not Step-to-step; not Visits “stopped at”; not people.'
                }
                unavailable={PRODUCT_PATHS_UNAVAILABLE}
                error={productPathsError}
                loading={productPathsLoading}
                loadingLabel="Loading journeys…"
                items={stopBarsFromJourneys(productPaths?.journeys, { hideApp: !isAdmin })}
                emptyMessage={
                  (productPaths?.journeys || []).length
                    ? 'No last pages after the noise floor.'
                    : 'No journeys in range. Raw steps are kept 72 hours.'
                }
              />
              {isAdmin && openJourneyPayload ? (
                visitTimelineQuery.error && timelineIsCurrent ? (
                  <p className="chart-empty">{visitTimelineErrorCopy(visitTimelineQuery.error)}</p>
                ) : visitTimelineQuery.loading || !timelineIsCurrent ? (
                  <p className="chart-hint">Loading visits…</p>
                ) : (visitTimelineQuery.data?.adminVisitTimelines || []).length === 0 ? (
                  <p className="chart-empty">No visits kept for this path.</p>
                ) : (
                  <ol className="visit-timeline-list">
                    {(visitTimelineQuery.data?.adminVisitTimelines || []).map((visit) => (
                      <li key={visit.id}>
                        {(visit.hops || []).map((hop) => visitHopLabel(hop)).join(' → ')}
                      </li>
                    ))}
                  </ol>
                )
              ) : null}
            </div>

            <AnalyticsBars
              title="Empty / stuck"
              hint="Empty states and blocked forms. Mostly app-lane."
              unavailable={PRODUCT_ANALYTICS_UNAVAILABLE}
              error={productAnalyticsError}
              loading={productAnalyticsLoading}
              loadingLabel="Loading stuck events…"
              items={[
                ...(productAnalytics?.emptyStates || []),
                ...(productAnalytics?.formBlocked || []),
              ].map((row) => analyticsBarItem(row))}
              emptyMessage="No stuck-state events in range."
            />
          </div>

          <AnalyticsBars
            title="Step to step"
            hint="From→to counts for the selected range. Kept after raw steps are purged."
            unavailable={PRODUCT_PATHS_UNAVAILABLE}
            error={productPathsError}
            loading={productPathsLoading}
            loadingLabel="Loading path pairs…"
            items={(productPaths?.pathPairs || []).map((row) => pathPairBarItem(row))}
            emptyMessage="No path-pair counts in range."
          />

          <p className="module-footnote">
            Journeys follow the selected range. Raw steps are kept 72 hours.
          </p>
        </>
      )}

      {activeTab === 'visits' && (
        <VisitList
          isAdmin={isAdmin}
          loading={visitSessionsQuery.loading}
          error={visitSessionsQuery.error}
          rows={visitSessionsQuery.data?.adminVisitSessions}
          loaded={Boolean(visitSessionsQuery.data) || Boolean(visitSessionsQuery.error)}
        />
      )}

      {activeTab === 'signups' && (
        <>
          <SignupFunnelTable
            title="Paid signup"
            hint={`Event counts by channel. % is of the previous step. ${CONSENTED_FUNNEL_INTEGRITY_CAPTION}`}
            emptyMessage={PAID_FUNNEL_EMPTY}
            steps={PAID_FUNNEL_STEPS}
            funnelRows={funnelRows}
            analyticsError={productAnalyticsError}
            analyticsLoading={productAnalyticsLoading}
          />

          <SignupFunnelTable
            title="Free signup"
            hint={`Event counts by channel. % is of the previous step. ${CONSENTED_FUNNEL_INTEGRITY_CAPTION}`}
            emptyMessage={FREE_FUNNEL_EMPTY}
            steps={FREE_FUNNEL_STEPS}
            funnelRows={funnelRows}
            analyticsError={productAnalyticsError}
            analyticsLoading={productAnalyticsLoading}
          />

          <SignupDiagnosis
            steps={PAID_FUNNEL_STEPS}
            funnelRows={funnelRows}
            emptyStates={productAnalytics?.emptyStates}
            formBlocked={productAnalytics?.formBlocked}
            featureGates={productAnalytics?.featureGates}
            journeys={productPaths?.journeys}
            analyticsError={productAnalyticsError}
            analyticsLoading={productAnalyticsLoading}
            pathsError={productPathsError}
            pathsLoading={productPathsLoading}
          />

          <SignupActivation
            steps={activationData?.adminActivationFunnel}
            error={activationError}
            loading={activationLoading}
          />

          <AnalyticsBars
            title="Server conversions"
            hint={SERVER_CONVERSIONS_INTEGRITY_CAPTION}
            unavailable={PRODUCT_PATHS_UNAVAILABLE}
            error={productPathsError}
            loading={productPathsLoading}
            loadingLabel="Loading server conversions…"
            items={(productPaths?.serverConversions || []).map((row) => analyticsBarItem(row))}
            emptyMessage="No server conversion counts in range."
          />

          <section className="chart-card">
            <h2>Tier distribution</h2>
            <p className="chart-hint">Live snapshot (all users)</p>
            {tierLoading ? (
              <p className="chart-hint">Loading tiers…</p>
            ) : tierError ? (
              <p className="chart-empty">Could not load tier distribution.</p>
            ) : (
              <HorizontalBarChart items={tierBars} emptyMessage="No tier distribution data." />
            )}
          </section>

          <p className="module-footnote">
            Funnel % is this step’s event count divided by the previous step in that column, not unique
            people. Consented analytics may under-count. Paid checkout completed includes in-app
            upgrades. Free completed divided by plan step mixes every intent. A zero before channel
            collect is live is a missing event, not a drop. Empty channel counts as Other. Server
            conversions are API counts by plan.
          </p>
        </>
      )}

      {activeTab === 'friction' && (
        <>
          <AnalyticsBars
            title="Feature gates"
            hint="Upgrade walls shown in the app."
            unavailable={PRODUCT_ANALYTICS_UNAVAILABLE}
            error={productAnalyticsError}
            loading={productAnalyticsLoading}
            loadingLabel="Loading feature gates…"
            items={(productAnalytics?.featureGates || []).map((row) => analyticsBarItem(row))}
            emptyMessage="No feature-gate events in range."
          />

          <section className="chart-card">
            <h2>Limit hits by action</h2>
            {limitHitLoading ? (
              <p className="chart-hint">Loading limit hits…</p>
            ) : limitHitError ? (
              <p className="chart-empty">Could not load limit hits.</p>
            ) : (
              <>
                <p className="chart-hint">
                  {formatNumber(limitHits?.total ?? 0)} limit_exceeded events in range
                </p>
                <HorizontalBarChart items={limitHitBars} emptyMessage="No limit hits in range." />
              </>
            )}
          </section>

          <section className="chart-card">
            <h2>Violations over time</h2>
            {violationLoading ? (
              <p className="chart-hint">Loading violations…</p>
            ) : violationError ? (
              <p className="chart-empty">Could not load violation totals.</p>
            ) : (
              <>
                <p className="chart-hint">
                  Daily counts from tier restriction logs · total{' '}
                  {formatNumber(violations?.total ?? 0)}
                </p>
                <PeriodDelta current={violations?.total} prior={priorViolations?.total} />
              </>
            )}
            {violationDayLoading ? (
              <p className="chart-hint">Loading violation trend…</p>
            ) : violationDayError ? (
              <p className="chart-empty">Could not load violation trend.</p>
            ) : (
              <TimeSeriesChart
                series={violationDaySeries}
                field="count"
                dateField="date"
                className="time-series-chart time-series-chart-violations"
                emptyMessage="No daily violation counts in range."
                ariaLabel="Daily tier restriction counts"
              />
            )}
          </section>

          {!violationLoading && !violationError && !hasViolationData && (
            <p className="module-status">No violation data in range.</p>
          )}

          <div className="product-tables">
            {violationTables.map(([title, rows, emptyMessage]) => (
              <section className="chart-card" key={title}>
                <h2>{title}</h2>
                {violationLoading ? (
                  <p className="chart-hint">Loading violations…</p>
                ) : violationError ? (
                  <p className="chart-empty">Could not load violations.</p>
                ) : rows.length === 0 ? (
                  <p className="chart-empty">{emptyMessage}</p>
                ) : (
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col">Name</th>
                          <th scope="col">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(([name, count]) => (
                          <tr key={name}>
                            <td>{name}</td>
                            <td>{formatNumber(count)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>

          <p className="module-footnote">
            Limit hits and the daily violation trend come from tier restriction logs.
          </p>
        </>
      )}

      {activeTab === 'heatmap' && <HeatmapPane range={range} />}
    </ModuleShell>
  );
}
